import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Download, CheckCircle2, AlertTriangle, CreditCard, ExternalLink, Loader2 } from "lucide-react";
import { StripeAutopayCard, PayPalCard, AutopayActivation, AutopayStatusBanner } from "@/components/pay/AutopayWidgets";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CardPayment = ({ token, card, balance }) => {
  const [busy, setBusy] = useState(false);
  const start = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/pay/${token}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      alert(e.response?.data?.detail || "Card payment is temporarily unavailable. Please use another payment option.");
      setBusy(false);
    }
  };
  return (
    <div className="rounded-2xl border-2 border-slate-700 bg-white p-5" data-testid="pay-card-section">
      <p className="font-bold text-slate-800 flex items-center gap-2"><CreditCard className="w-5 h-5" /> Pay by credit / debit card</p>
      <p className="text-xs text-slate-500 mt-1">Secure checkout powered by Stripe (Visa, Mastercard, Amex, Discover).</p>
      <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900" data-testid="pay-card-fee-notice">
        <p className="font-semibold">Card processing fee: {card.fee_label}</p>
        <div className="mt-1 space-y-0.5 text-xs">
          <div className="flex justify-between"><span>Invoice balance</span><span>{money(balance)}</span></div>
          <div className="flex justify-between"><span>Processing fee ({card.fee_label})</span><span data-testid="pay-card-fee-amount">+{money(card.fee_amount)}</span></div>
          <div className="flex justify-between font-bold text-sm border-t border-amber-300 pt-1 mt-1"><span>Total charged to your card</span><span data-testid="pay-card-total">{money(card.total_with_fee)}</span></div>
        </div>
        <p className="mt-2 text-xs">The fee covers card transaction costs. Cash App and Zelle above have no fee.</p>
      </div>
      <button onClick={start} disabled={busy} className="mt-3 w-full flex items-center justify-center gap-2 rounded-xl bg-slate-800 hover:bg-slate-900 disabled:opacity-60 text-white py-3.5 font-semibold" data-testid="pay-card-button">
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />} {busy ? "Redirecting to secure checkout…" : `Pay ${money(card.total_with_fee)} by card`}
      </button>
    </div>
  );
};

const CheckoutResult = ({ token, sessionId }) => {
  const [state, setState] = useState({ status: "checking", tries: 0 });
  useEffect(() => {
    let cancelled = false;
    const poll = async (tries) => {
      try {
        const { data } = await axios.get(`${API}/public/pay/${token}/checkout-status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") return setState({ status: "paid", data });
        if (data.payment_status === "expired" || data.payment_status === "failed") return setState({ status: "failed" });
      } catch (e) { /* keep polling */ }
      if (tries >= 8) return setState({ status: "pending" });
      setTimeout(() => poll(tries + 1), 2000);
    };
    poll(0);
    return () => { cancelled = true; };
  }, [token, sessionId]);
  const cls = { checking: "bg-blue-50 border-blue-200 text-blue-900", paid: "bg-green-50 border-green-300 text-green-900", pending: "bg-amber-50 border-amber-300 text-amber-900", failed: "bg-red-50 border-red-300 text-red-900" }[state.status];
  const text = {
    checking: "Confirming your card payment…",
    paid: `Card payment received — thank you! ${state.data ? `${money(state.data.total_charged)} charged (includes ${money(state.data.fee)} processing fee).` : ""} A receipt is on its way to your email.`,
    pending: "Your payment is still being confirmed by the bank. This page will reflect it shortly — no need to pay again.",
    failed: "The card payment was not completed. You can try again or use Cash App / Zelle.",
  }[state.status];
  return <div className={`rounded-2xl border p-4 text-sm font-medium flex items-center gap-2 ${cls}`} data-testid={`checkout-result-${state.status}`}>{state.status === "checking" ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}{text}</div>;
};

const MethodCard = ({ m }) => (
  <div className="rounded-2xl border-2 p-5 text-center" style={{ borderColor: m.color, background: m.bg }} data-testid={`pay-method-${m.key}`}>
    <p className="font-bold text-base" style={{ color: m.dark }}>{m.title}</p>
    <a href={m.link} target="_blank" rel="noopener noreferrer"><img src={`data:image/png;base64,${m.qr_b64}`} alt={`${m.title} QR code`} className="w-44 h-44 mx-auto my-3 rounded-lg bg-white" /></a>
    <p className="font-bold break-all" style={{ color: m.color }}>{m.headline}</p>
    <p className="text-xs text-slate-600 mt-1">{m.note}</p>
    {m.key === "cash_app" && (
      <a href={m.link} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-2 rounded-full px-5 py-2 text-white font-semibold text-sm" style={{ background: m.color }} data-testid="open-cash-app-button">
        Open Cash App <ExternalLink className="w-4 h-4" />
      </a>
    )}
  </div>
);

export default function PayInvoice() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const setupSessionId = params.get("setup_session_id");
  const autopayFlag = params.get("autopay");
  const canceled = params.get("canceled");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    axios.get(`${API}/public/pay/${token}`).then((r) => setData(r.data)).catch((e) => setError(e.response?.data?.detail || "This payment link is not valid"));
  }, [token, refresh]);
  useEffect(() => {
    if (!sessionId) return undefined;
    const t = setTimeout(() => setRefresh((r) => r + 1), 5000);
    return () => clearTimeout(t);
  }, [sessionId]);

  if (error) return <div className="min-h-screen flex items-center justify-center bg-slate-100 p-6"><p className="text-slate-700 text-center" data-testid="pay-error">{error}</p></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-slate-100"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>;

  const { company, invoice: inv, methods, payments, card } = data;
  const paid = inv.balance_due <= 0;
  const autopayOffered = data.autopay_available && !data.mandate;
  return (
    <div className="min-h-screen bg-slate-100 py-6 px-4" data-testid="pay-page">
      <div className="max-w-xl mx-auto space-y-4">
        {sessionId && <CheckoutResult token={token} sessionId={sessionId} />}
        {autopayFlag && (sessionId || setupSessionId) && <AutopayActivation token={token} sessionId={sessionId || setupSessionId} />}
        {data.is_recurring && <AutopayStatusBanner mandate={data.mandate} status={data.autopay_status} />}
        {canceled && !paid && <div className="rounded-2xl border border-slate-300 bg-white p-4 text-sm text-slate-700" data-testid="checkout-canceled">Card checkout was canceled — no charge was made. You can try again or use Cash App / Zelle below.</div>}
        <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
          {company.logo_b64 && <img src={`data:image/jpeg;base64,${company.logo_b64}`} alt={company.name} className="h-16 mx-auto mb-3 object-contain" />}
          <h1 className="text-xl font-bold text-slate-800" data-testid="pay-company-name">{company.name}</h1>
          <p className="text-xs text-slate-500">{company.address}{company.phone ? ` · ${company.phone}` : ""}</p>
        </div>

        <div className={`rounded-2xl shadow-lg p-6 text-white ${paid ? "bg-gradient-to-br from-emerald-600 to-green-700" : inv.overdue ? "bg-gradient-to-br from-rose-600 to-red-700" : "bg-gradient-to-br from-blue-700 to-indigo-800"}`} data-testid="pay-balance-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide opacity-80">Invoice</p>
              <p className="text-2xl font-bold" data-testid="pay-invoice-number">{inv.invoice_number}</p>
              <p className="text-sm opacity-90">{inv.customer_name}</p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-wide opacity-80">{paid ? "Paid in full" : "Balance due"}</p>
              <p className="text-3xl font-bold" data-testid="pay-balance">{paid ? money(inv.total) : money(inv.balance_due)}</p>
              <p className="text-xs opacity-90">{paid ? `Paid ${inv.paid_at || ""}` : `Due ${inv.due_date}${inv.overdue ? " · overdue" : ""}`}</p>
            </div>
          </div>
          {paid && <p className="mt-4 flex items-center gap-2 text-sm font-medium" data-testid="pay-paid-banner"><CheckCircle2 className="w-5 h-5" /> Thank you — this invoice has been paid in full.</p>}
          {!paid && inv.overdue && <p className="mt-4 flex items-center gap-2 text-sm font-medium"><AlertTriangle className="w-5 h-5" /> This invoice is past due. Thank you for taking care of it today.</p>}
        </div>

        {!paid && (
          <>
            <p className="text-center text-sm font-semibold text-slate-600 pt-2">Scan or tap to pay {money(inv.balance_due)} — no fees</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="pay-methods">
              {methods.map((m) => <MethodCard key={m.key} m={m} />)}
            </div>
            {card ? (autopayOffered ? <StripeAutopayCard token={token} card={card} balance={inv.balance_due} data={data} /> : <CardPayment token={token} card={card} balance={inv.balance_due} />) : inv.stripe_invoice_url && (
              <a href={inv.stripe_invoice_url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 rounded-2xl bg-slate-800 text-white py-4 font-semibold shadow-lg" data-testid="pay-card-button">
                <CreditCard className="w-5 h-5" /> Pay by card
              </a>
            )}
            {data.paypal && <PayPalCard token={token} data={data} balance={inv.balance_due} onPaid={() => setTimeout(() => setRefresh((r) => r + 1), 1500)} />}
          </>
        )}

        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-slate-800">Invoice details</h2>
            <a href={`${API}/public/pay/${token}/pdf`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-blue-700 font-medium" data-testid="pay-download-pdf"><Download className="w-4 h-4" /> PDF</a>
          </div>
          <p className="text-xs text-slate-500 mb-3">Issued {inv.created_at} · Due {inv.due_date}</p>
          <div className="divide-y text-sm" data-testid="pay-items">
            {inv.items.map((it, i) => (
              <div key={i} className="flex justify-between gap-3 py-2">
                <span className="text-slate-700">{it.description}<span className="text-slate-400"> × {it.quantity}</span></span>
                <span className="font-medium text-slate-800 whitespace-nowrap">{money(it.amount)}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t text-sm space-y-1">
            <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{money(inv.subtotal)}</span></div>
            <div className="flex justify-between text-slate-600"><span>Tax</span><span>{money(inv.tax)}</span></div>
            <div className="flex justify-between font-bold text-slate-800 text-base"><span>Total</span><span data-testid="pay-total">{money(inv.total)}</span></div>
            {inv.amount_paid > 0 && <div className="flex justify-between text-green-700"><span>Paid to date</span><span data-testid="pay-paid-to-date">-{money(inv.amount_paid)}</span></div>}
            {!paid && <div className="flex justify-between font-bold text-red-700 text-base"><span>Balance due</span><span>{money(inv.balance_due)}</span></div>}
          </div>
          {payments.length > 0 && (
            <div className="mt-4 text-xs text-slate-500" data-testid="pay-history">
              <p className="font-semibold text-slate-600 mb-1">Payments received</p>
              {payments.map((p, i) => <p key={i}>{p.date} · {p.method_label} · {money(p.amount)}</p>)}
            </div>
          )}
        </div>
        <p className="text-center text-xs text-slate-400 pb-6">Questions? {company.phone ? `Call ${company.phone}` : ""}{company.email ? ` · ${company.email}` : ""}</p>
      </div>
    </div>
  );
}
